import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import {
  Zap, Plus, CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  Clock, Settings, RefreshCw, Printer
} from 'lucide-react'
import filtrosBateriasService from '../../services/filtrosBateriasService'
import api from '../../api/axios'
import { useToast } from '../../hooks/useToast'

const REGRAS_POS_HORARIO = [
  { id: 'CABECAS_DESC', label: 'Ao final, do maior para o menor nº de cabeças' },
  { id: 'CABECAS_ASC', label: 'Ao final, do menor para o maior nº de cabeças' },
  { id: 'CADASTRO', label: 'Ao final, na ordem de cadastro' },
  { id: 'SEM_REGRA', label: 'Sem tratamento especial (seguem o filtro normalmente)' },
]

export default function OrdemEntradaBaterias({ leilaoId, organizadorId, leilaoAtual, podeEditar, onHorarioSalvo }) {
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()

  // Horário limite de chegada + regra dos lotes pós-horário (horário de Brasília)
  const [horario, setHorario] = useState(leilaoAtual?.horario_limite_entrada || '')
  const [regraPos, setRegraPos] = useState(leilaoAtual?.regra_pos_horario || 'CABECAS_DESC')
  const [salvandoHorario, setSalvandoHorario] = useState(false)
  const [modoAplicar, setModoAplicar] = useState('manter_manuais')

  // Lotes novos entram sozinhos na ordem? (desligado = ficam como "lotes disponíveis" na Ordem Manual)
  const encaixeAtivo = leilaoAtual?.encaixe_automatico !== false
  const [salvandoEncaixe, setSalvandoEncaixe] = useState(false)
  const alternarEncaixe = async () => {
    const msg = encaixeAtivo
      ? 'Desligar a entrada automática?\n\nOs lotes cadastrados daqui em diante NÃO entrarão na ordem. Eles ficarão em "Lotes disponíveis" na Ordem Manual até você posicioná-los.'
      : 'Ligar a entrada automática?\n\nOs lotes cadastrados daqui em diante voltarão a entrar sozinhos na ordem de entrada (na bateria certa do filtro aplicado).'
    if (!window.confirm(msg)) return
    setSalvandoEncaixe(true)
    try {
      const r = await filtrosBateriasService.salvarEncaixeAutomatico(organizadorId, leilaoId, !encaixeAtivo)
      onHorarioSalvo?.(r)
      toast.success(r.encaixe_automatico
        ? 'Lotes novos voltam a entrar automaticamente na ordem'
        : 'Lotes novos ficarão disponíveis para você posicionar na Ordem Manual')
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar configuração')
    } finally {
      setSalvandoEncaixe(false)
    }
  }

  useEffect(() => {
    setHorario(leilaoAtual?.horario_limite_entrada || '')
    setRegraPos(leilaoAtual?.regra_pos_horario || 'CABECAS_DESC')
  }, [leilaoAtual?.horario_limite_entrada, leilaoAtual?.regra_pos_horario])

  const horarioAlterado =
    horario !== (leilaoAtual?.horario_limite_entrada || '') ||
    regraPos !== (leilaoAtual?.regra_pos_horario || 'CABECAS_DESC')

  const salvarHorario = async () => {
    setSalvandoHorario(true)
    try {
      const r = await filtrosBateriasService.salvarHorarioLimite(organizadorId, leilaoId, {
        horario_limite_entrada: horario || null,
        regra_pos_horario: regraPos,
      })
      onHorarioSalvo?.(r)
      toast.success('Horário limite salvo — vale para os próximos lotes cadastrados')
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar horário limite')
    } finally {
      setSalvandoHorario(false)
    }
  }

  const [modalAberto, setModalAberto] = useState(false)
  const [filtroSelecionado, setFiltroSelecionado] = useState('')
  const [preview, setPreview] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [expandedBaterias, setExpandedBaterias] = useState({})

  // Filtros do organizador
  const { data: filtros = [] } = useQuery(
    ['filtros-baterias', organizadorId],
    () => filtrosBateriasService.listar(organizadorId),
    { enabled: !!organizadorId }
  )

  // Ordem atual do leilão
  const { data: ordemAtual = [], isLoading: loadingOrdem } = useQuery(
    ['ordem-entrada', leilaoId],
    () => api.get(`/organizadores/ordem-entrada/${leilaoId}`).then(r => r.data),
    // Sempre rebusca ao voltar para a tela: a Ordem Manual altera a ordem fora deste cache
    { enabled: !!leilaoId && !!organizadorId, refetchOnMount: 'always' }
  )

  const qtdManuais = ordemAtual.filter(o => o.metodo_gerado === 'MANUAL').length
  const temManuais = qtdManuais > 0

  const { mutate: aplicar, isLoading: aplicando } = useMutation(
    () => filtrosBateriasService.aplicar(organizadorId, filtroSelecionado, leilaoId, temManuais ? modoAplicar : 'redefinir'),
    {
      onSuccess: (data) => {
        toast.success(
          `Ordem gerada: ${data.total_lotes} lotes em ${data.total_baterias} bateria(s)` +
          (data.manuais_mantidos ? ` · ${data.manuais_mantidos} ajuste(s) manual(is) mantido(s)` : '')
        )
        qc.invalidateQueries(['ordem-entrada', leilaoId])
        setModalAberto(false)
        setPreview(null)
        setFiltroSelecionado('')
      },
      onError: () => toast.error('Erro ao aplicar filtro'),
    }
  )

  const handlePreview = async () => {
    if (!filtroSelecionado) return
    setLoadingPreview(true)
    setPreview(null)
    try {
      const result = await filtrosBateriasService.validar(organizadorId, filtroSelecionado, leilaoId)
      setPreview(result)
      setExpandedBaterias(Object.fromEntries(result.baterias.map(b => [b.bateria, true])))
    } catch {
      toast.error('Erro ao carregar preview')
    } finally {
      setLoadingPreview(false)
    }
  }

  const filtrosAtivos = filtros.filter(f => f.ativo)
  const filtroEscolhido = filtros.find(f => f.id.toString() === filtroSelecionado.toString())

  // Separar ordem por baterias (detecta agrupamento)
  const posHorario = ordemAtual.filter(o => o.pos_horario_limite)
  const normais = ordemAtual.filter(o => !o.pos_horario_limite)

  return (
    <div className="space-y-4">
      {/* Barra de ações */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        {podeEditar ? (
          <>
            <button
              onClick={() => setModalAberto(true)}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition"
            >
              <Zap size={16} />
              Aplicar filtro automático
            </button>
            <button
              onClick={() => navigate('/ordem-entrada/filtros-automaticos')}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium text-sm hover:bg-gray-50 transition"
            >
              <Settings size={16} />
              Gerenciar filtros
            </button>
            <button
              onClick={() => window.open(`/catalogo-leilao/${leilaoId}`, '_blank')}
              disabled={ordemAtual.length === 0}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium text-sm transition"
            >
              <Printer size={16} />
              Catálogo Leilão (Fazenda)
            </button>
          </>
        ) : (
          <p className="text-sm text-gray-500">Você tem permissão apenas para visualizar a ordem de entrada.</p>
        )}
        <div className="ml-auto text-sm text-gray-500">
          {ordemAtual.length} lote(s) na ordem
          {posHorario.length > 0 && (
            <span className="ml-2 text-orange-600 font-medium">· {posHorario.length} pós-horário</span>
          )}
        </div>
      </div>

      {/* Entrada automática de lotes novos */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={16} className="text-purple-500" />
              <h3 className="text-sm font-semibold text-gray-800">Lotes novos entram automaticamente na ordem</h3>
            </div>
            <p className="text-xs text-gray-500">
              {encaixeAtivo
                ? 'Ligado: ao cadastrar um lote, ele já é encaixado na bateria certa do filtro aplicado.'
                : 'Desligado: lotes cadastrados ficam fora da ordem, na lista de “Lotes disponíveis” da Ordem Manual, para você posicionar.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={encaixeAtivo}
            disabled={!podeEditar || salvandoEncaixe}
            onClick={alternarEncaixe}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${encaixeAtivo ? 'bg-purple-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${encaixeAtivo ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {/* Horário limite de chegada */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={16} className="text-orange-500" />
          <h3 className="text-sm font-semibold text-gray-800">Horário limite de chegada</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Lotes cadastrados após esse horário (horário de Brasília, no dia do leilão) são marcados como pós-horário.
          Vale para os lotes cadastrados a partir de agora.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Horário</label>
            <input
              type="time"
              value={horario}
              disabled={!podeEditar}
              onChange={(e) => setHorario(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-32 disabled:bg-gray-50"
            />
          </div>
          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">O que acontece com eles</label>
            <select
              value={regraPos}
              disabled={!podeEditar}
              onChange={(e) => setRegraPos(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-50"
            >
              {REGRAS_POS_HORARIO.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          {podeEditar && (
            <>
              <button
                onClick={salvarHorario}
                disabled={!horarioAlterado || salvandoHorario}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
              >
                {salvandoHorario ? 'Salvando...' : 'Salvar'}
              </button>
              {horario && (
                <button
                  onClick={() => setHorario('')}
                  className="text-sm text-red-500 hover:underline py-2"
                >
                  Remover limite
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lista da ordem atual */}
      {loadingOrdem ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 h-14 animate-pulse" />
          ))}
        </div>
      ) : ordemAtual.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
          <Zap size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-medium">Nenhuma ordem gerada ainda</p>
          {podeEditar && (
            <button
              onClick={() => setModalAberto(true)}
              className="mt-4 text-purple-600 hover:underline text-sm font-medium"
            >
              Aplicar filtro automático
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Ordem de entrada gerada</span>
            {podeEditar && (
              <button
                onClick={() => setModalAberto(true)}
                className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                <RefreshCw size={13} /> Regenerar
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {normais.map((item, idx) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                <span className="w-8 text-center text-sm font-mono text-gray-400">{item.posicao}</span>
                <div className="w-12 text-xs font-semibold text-gray-700 font-mono">{item.lote_numero}</div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-800 truncate">{item.lote_descricao}</span>
                </div>
                <span className="text-xs text-gray-500">{item.lote_qtd} cab.</span>
                {item.raca_nome && (
                  <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded hidden sm:block">{item.raca_nome}</span>
                )}
                {item.fazenda_nome && (
                  <span className="text-xs text-gray-400 hidden md:block truncate max-w-[140px]">{item.fazenda_nome}</span>
                )}
              </div>
            ))}

            {posHorario.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-4 py-2 bg-orange-50">
                  <Clock size={14} className="text-orange-500" />
                  <span className="text-xs font-semibold text-orange-700">Pós-horário limite — {posHorario.length} lote(s)</span>
                </div>
                {posHorario.map(item => (
                  <div key={item.id} className="flex items-center gap-3 px-4 py-3 bg-orange-50/40">
                    <span className="w-8 text-center text-sm font-mono text-gray-400">{item.posicao}</span>
                    <div className="w-12 text-xs font-semibold text-gray-700 font-mono">{item.lote_numero}</div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-700 truncate">{item.lote_descricao}</span>
                    </div>
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                      Pós-horário
                    </span>
                    <span className="text-xs text-gray-500">{item.lote_qtd} cab.</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Aplicar Filtro */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Aplicar Filtro Automático</h3>
                <p className="text-xs text-gray-500 mt-0.5">{leilaoAtual?.nome}</p>
              </div>
              <button
                onClick={() => { setModalAberto(false); setPreview(null); setFiltroSelecionado('') }}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Seletor de filtro */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Selecionar filtro</label>
                {filtrosAtivos.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <p className="text-sm text-gray-500 mb-2">Nenhum filtro ativo cadastrado</p>
                    <button
                      onClick={() => { setModalAberto(false); navigate('/ordem-entrada/filtros-automaticos/novo') }}
                      className="text-purple-600 hover:underline text-sm font-medium"
                    >
                      Criar filtro
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filtrosAtivos.map(f => (
                      <label
                        key={f.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${
                          filtroSelecionado.toString() === f.id.toString()
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="filtro"
                          value={f.id}
                          checked={filtroSelecionado.toString() === f.id.toString()}
                          onChange={() => { setFiltroSelecionado(f.id); setPreview(null) }}
                          className="sr-only"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-800">{f.nome}</div>
                          <div className="text-xs text-gray-500">
                            {f.tipo === 'SEXO'
                              ? `Por Sexo · ${f.sexo_config?.sexo_primeiro === 'MACHO' ? 'Machos' : 'Fêmeas'} primeiro`
                              : `Personalizado · ${f.regras?.length || 0} regra(s)`}
                          </div>
                        </div>
                        {filtroSelecionado.toString() === f.id.toString() && (
                          <CheckCircle size={18} className="text-purple-600 flex-shrink-0" />
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              {filtroSelecionado && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Pré-visualização</label>
                    <button
                      onClick={handlePreview}
                      disabled={loadingPreview}
                      className="flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
                    >
                      <RefreshCw size={14} className={loadingPreview ? 'animate-spin' : ''} />
                      {loadingPreview ? 'Calculando...' : 'Calcular preview'}
                    </button>
                  </div>

                  {preview && (
                    <div className="space-y-2">
                      {/* Resumo */}
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { l: 'Total', v: preview.total_lotes, c: 'blue' },
                          { l: 'Alocados', v: preview.total_alocados, c: 'green' },
                          { l: 'Sem bateria', v: preview.total_sem_bateria, c: preview.total_sem_bateria > 0 ? 'yellow' : 'gray' },
                          { l: 'Pós-horário', v: preview.total_pos_horario, c: preview.total_pos_horario > 0 ? 'orange' : 'gray' },
                        ].map(s => (
                          <div key={s.l} className={`bg-${s.c}-50 border border-${s.c}-100 rounded-lg p-2 text-center`}>
                            <div className={`text-xl font-bold text-${s.c}-700`}>{s.v}</div>
                            <div className={`text-xs text-${s.c}-600`}>{s.l}</div>
                          </div>
                        ))}
                      </div>

                      {/* Baterias */}
                      {preview.baterias.map(b => (
                        <div key={b.bateria} className="border border-gray-200 rounded-xl overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setExpandedBaterias(prev => ({ ...prev, [b.bateria]: !prev[b.bateria] }))}
                            className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-700 text-sm">{b.label}</span>
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{b.lotes.length}</span>
                            </div>
                            {expandedBaterias[b.bateria] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                          {expandedBaterias[b.bateria] && (
                            <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                              {b.lotes.map(l => (
                                <div key={l.lote_id} className="px-4 py-2 flex items-center gap-2 text-sm">
                                  <span className="font-mono text-xs text-gray-400 w-10">{l.numero}</span>
                                  <span className="flex-1 text-gray-700 truncate">{l.tipo_animal} {l.sexo}</span>
                                  <span className="text-xs text-gray-400">{l.qtd}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      {preview.lotes_sem_bateria.length > 0 && (
                        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                          <AlertCircle size={14} className="text-yellow-600" />
                          <span className="text-xs text-yellow-700">{preview.lotes_sem_bateria.length} lote(s) sem bateria → vão ao final</span>
                        </div>
                      )}
                      {preview.lotes_pos_horario.length > 0 && (
                        <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                          <Clock size={14} className="text-orange-600" />
                          <span className="text-xs text-orange-700">{preview.lotes_pos_horario.length} lote(s) pós-horário → vão ao final</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Reaplicar com ajustes manuais existentes */}
              {temManuais && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 space-y-2">
                  <p className="text-sm font-medium text-blue-900">
                    Esta ordem tem {qtdManuais} lote(s) ajustado(s) manualmente. O que fazer?
                  </p>
                  {[
                    { id: 'manter_manuais', t: 'Manter os ajustes manuais', d: 'Lotes ✋ Manual ficam onde estão; o filtro reorganiza só os demais.' },
                    { id: 'redefinir', t: 'Redefinir tudo', d: 'Refaz a ordem inteira pelo filtro e descarta os ajustes manuais.' },
                  ].map(o => (
                    <label key={o.id} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="modo-aplicar"
                        checked={modoAplicar === o.id}
                        onChange={() => setModoAplicar(o.id)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm text-gray-800 font-medium">{o.t}</span>
                        <span className="block text-xs text-gray-500">{o.d}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {/* Ações */}
              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button
                  onClick={() => { setModalAberto(false); setPreview(null); setFiltroSelecionado('') }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl font-medium text-sm hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => aplicar()}
                  disabled={!filtroSelecionado || aplicando}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-2.5 rounded-xl font-medium text-sm transition"
                >
                  {aplicando ? 'Aplicando...' : 'Aplicar e gerar ordem'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
